import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowLeft, Sparkles, Phone, MapPin, Clock, Star, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BeautyDemo = () => {
  const { t } = useTranslation();
  const [selectedService, setSelectedService] = useState(null);

  const handleBooking = () => {
    toast.success(`✅ ${t('toast.bookingSuccess')}`, {
      description: t('toast.bookingSuccessDesc')
    });
  };

  const services = [
    { id: 1, icon: Sparkles, name: t('beautyDemo.service1'), desc: t('beautyDemo.service1Desc'), price: '79€' },
    { id: 2, icon: Sparkles, name: t('beautyDemo.service2'), desc: t('beautyDemo.service2Desc'), price: '45€' },
    { id: 3, icon: Sparkles, name: t('beautyDemo.service3'), desc: t('beautyDemo.service3Desc'), price: '89€' },
    { id: 4, icon: Sparkles, name: t('beautyDemo.service4'), desc: t('beautyDemo.service4Desc'), price: '35€' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Back Link */}
      <div className="fixed top-4 left-4 z-50">
        <Link to="/portfolio">
          <Button variant="outline" className="bg-white/90 hover:bg-white shadow-lg">
            <ArrowLeft size={16} className="mr-2" />
            {t('common.backToPortfolio')}
          </Button>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-pink-100">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center">
              <Sparkles className="text-white" size={20} />
            </div>
            <div>
              <span className="font-semibold text-gray-900">{t('beautyDemo.name')}</span>
              <p className="text-xs text-gray-500">{t('beautyDemo.tagline')}</p>
            </div>
          </div>
          <Button onClick={handleBooking} className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white">
            <Calendar size={16} className="mr-2" />
            {t('beautyDemo.bookButton')}
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center pt-16">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1920&q=80')] bg-cover bg-center opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-100 text-pink-600 text-sm font-medium mb-6">
              <Star className="fill-pink-600" size={16} />
              {t('beautyDemo.tagline')}
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              {t('beautyDemo.heroTitle')}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {t('beautyDemo.heroDesc')}
            </p>
            <Button onClick={handleBooking} size="lg" className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white">
              <Calendar size={20} className="mr-2" />
              {t('beautyDemo.bookButton')}
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Unsere Services</h2>
            <p className="text-gray-600">Professionelle Beauty-Behandlungen</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <Card key={service.id} className="border-2 border-pink-100 hover:border-pink-300 transition-all hover:shadow-lg cursor-pointer">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center mx-auto mb-4">
                    <service.icon className="text-pink-500" size={28} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{service.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{service.desc}</p>
                  <div className="text-2xl font-bold text-pink-600">{service.price}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Kundenstimmen</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-yellow-400" size={18} />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">{t('beautyDemo.testimonial1')}</p>
                <p className="font-semibold text-gray-900">Sarah M.</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-yellow-400" size={18} />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">{t('beautyDemo.testimonial2')}</p>
                <p className="font-semibold text-gray-900">Julia K.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('beautyDemo.contactTitle')}</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div>
              <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-3">
                <Phone className="text-pink-600" size={20} />
              </div>
              <p className="text-gray-600">+49 123 456789</p>
            </div>
            <div>
              <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-3">
                <MapPin className="text-pink-600" size={20} />
              </div>
              <p className="text-gray-600">Musterstraße 123, Berlin</p>
            </div>
            <div>
              <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-3">
                <Clock className="text-pink-600" size={20} />
              </div>
              <p className="text-gray-600">{t('beautyDemo.monday')}: 9-19h</p>
              <p className="text-gray-600">{t('beautyDemo.weekend')}: 10-16h</p>
            </div>
          </div>
          <Button onClick={handleBooking} size="lg" className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white">
            {t('beautyDemo.bookButton')}
          </Button>
        </div>
      </section>

      {/* Demo Note */}
      <div className="fixed bottom-4 right-4 bg-white border-2 border-gray-200 rounded-lg p-3 shadow-lg text-sm text-gray-600">
        <p className="font-semibold">{t('common.demoProject')}</p>
      </div>
    </div>
  );
};

export default BeautyDemo;